import { useRef } from 'react';

/**
 * Seo — injects per-page <title>, <meta name="description">, <link rel="canonical">,
 * and Open Graph tags into the document <head>.
 *
 * Sets title SYNCHRONOUSLY during render (not in an effect) so crawlers like 
 * Google, Bing, and Jet Octopus see correct values in their first JS snapshot.
 */
export default function Seo({ title, description, canonical, ogImage, robots }) {
  const prev = useRef({});

  // Set title synchronously during render — no effect means crawlers see it immediately
  if (typeof document !== 'undefined' && title && prev.current.title !== title) {
    document.title = title;
  }

  // Meta description — sync
  if (typeof document !== 'undefined' && description && prev.current.description !== description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
  }

  // Canonical — sync
  if (typeof document !== 'undefined' && canonical && prev.current.canonical !== canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }

  // Robots meta — sync (e.g. noindex,follow for ad landing pages)
  if (typeof document !== 'undefined' && robots !== undefined && prev.current.robots !== robots) {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', robots);
  }

  // OG tags — sync
  const setOg = (property, content) => {
    if (typeof document === 'undefined' || !content) return;
    let og = document.querySelector(`meta[property="${property}"]`);
    if (!og) {
      og = document.createElement('meta');
      og.setAttribute('property', property);
      document.head.appendChild(og);
    }
    og.setAttribute('content', content);
  };

  if (title && prev.current.ogTitle !== title) {
    setOg('og:title', title);
  }
  if (description && prev.current.ogDesc !== description) {
    setOg('og:description', description);
  }
  if (ogImage && prev.current.ogImage !== ogImage) {
    setOg('og:image', ogImage);
  }

  prev.current = { title, description, canonical, ogTitle: title, ogDesc: description, ogImage, robots };

  return null;
}